
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../components/AuthContext';
import { useLeague } from '../components/LeagueContext';
import { Card, Button, Input, Badge } from '../components/UI';
import { apiRequest } from '../utils/api';
import { Save, User as UserIcon, Shield, Lock, ExternalLink, Camera, Twitch, Youtube, Video, MessageSquare, AlertTriangle, HelpCircle, Palette } from 'lucide-react';
import { UserRole } from '../types';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { PageHero } from '../components/PageHero';

export const Profile: React.FC = () => {
    const { user, updateUserContext } = useAuth();
    const { teamThemeEnabled, toggleTeamTheme } = useLeague();
    const [formData, setFormData] = useState({
        username: '',
        eaId: '',
        discord_id: '', 
        password: '',
        confirmPassword: '',
        avatarUrl: '',
        twitch_name: '',
        youtube_handle: '',
        kick_name: '',
    });
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState<{type: 'success'|'error', text: string} | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (user) {
            setFormData(prev => ({ 
                ...prev, 
                username: user.username || '',
                eaId: user.eaId || '',
                discord_id: user.discord_id || '', 
                avatarUrl: user.avatarUrl || '',
                twitch_name: user.twitch_name || '',
                youtube_handle: user.youtube_handle || '',
                kick_name: user.kick_name || '',
            }));
        }
    }, [user]);

    const handleSave = async () => {
        setMsg(null);
        if (formData.password && formData.password !== formData.confirmPassword) {
            setMsg({ type: 'error', text: "Passwörter stimmen nicht überein." });
            return;
        }

        setLoading(true);
        try {
            const res = await apiRequest<{user: any}>('profile.php', 'POST', {
                id: user?.id,
                username: formData.username,
                eaId: formData.eaId,
                discord_id: formData.discord_id, 
                password: formData.password || undefined,
                avatarUrl: formData.avatarUrl,
                twitch_name: formData.twitch_name,
                youtube_handle: formData.youtube_handle,
                kick_name: formData.kick_name,
            });
            
            // Update Context
            if(res.user) {
                updateUserContext(res.user);
            }

            setMsg({ type: 'success', text: "Profil erfolgreich aktualisiert." });
            setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
        } catch (e: any) {
            setMsg({ type: 'error', text: e.message });
        } finally {
            setLoading(false);
        }
    };

    const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({ ...prev, avatarUrl: reader.result as string }));
            };
            reader.readAsDataURL(file);
        }
    };

    const getUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        return baseUrl ? `${baseUrl}/${url}` : url;
    };

    const getRoleColor = (role: string) => {
        if (role === UserRole.WEBADMIN) return 'blue';
        if (role === UserRole.ADMIN) return 'red';
        if (role === UserRole.STEWARD) return 'yellow';
        if (role === UserRole.DRIVER) return 'green';
        return 'gray';
    };

    if (!user) return null;

    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-fade-in pb-20">
            <PageHero
                eyebrow="Mein Cockpit"
                title="Konto & Profil"
                description="Verwalte deinen Fahrernamen, deine Ingame-ID, Kommunikationskanäle und die Darstellung deines öffentlichen Fahrerprofils."
                icon={UserIcon}
                accent="#22c55e"
                stats={[
                    { label: 'EA ID', value: formData.eaId || '–' },
                    { label: 'Rollen', value: user.roles.length },
                    { label: 'Streams', value: [formData.twitch_name, formData.youtube_handle, formData.kick_name].filter(Boolean).length },
                ]}
                actions={<Link to={`/driver/${user.id}`}><Button variant="secondary" icon={ExternalLink}>Öffentliches Profil</Button></Link>}
            />

            <Card className="rounded-[2rem] border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017] p-6 shadow-[0_26px_70px_rgba(0,0,0,0.24)] md:p-8">
                <div className="flex flex-col items-center gap-6 mb-8 pb-6 border-b border-gray-800">
                    {/* Avatar Upload */}
                    <div 
                        className="w-32 h-32 bg-gray-800 rounded-full flex items-center justify-center border-4 border-gray-700 relative group cursor-pointer overflow-hidden shadow-lg"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {formData.avatarUrl ? (
                            <img src={getUrl(formData.avatarUrl)} className="w-full h-full object-cover" alt="Avatar"/>
                        ) : (
                            <div className="text-4xl font-bold text-gray-500">{user.username.substring(0, 1).toUpperCase()}</div>
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                            <Camera className="text-white w-8 h-8"/>
                        </div>
                    </div>
                    <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleAvatarUpload} />

                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-white">{user.username}</h2>
                        <div className="flex gap-2 justify-center mt-2">
                            {user.roles.map(role => (
                                <Badge key={role} color={getRoleColor(role) as any}>{role}</Badge>
                            ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Klicke auf das Bild zum Ändern</p>
                    </div>
                </div>

                <div className="space-y-6">
                    {msg && (
                        <div className={`p-3 rounded border text-sm text-center font-bold ${msg.type === 'success' ? 'bg-green-900/30 border-green-800 text-green-400' : 'bg-red-900/30 border-red-800 text-red-400'}`}>
                            {msg.text}
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-6">
                        <div>
                            <Input 
                                label="Username" 
                                value={formData.username} 
                                onChange={e => setFormData({...formData, username: e.target.value})}
                                placeholder="Dein Anzeigename"
                            />
                            <div className="flex items-center gap-1 text-[10px] text-gray-500 mt-1">
                                <AlertTriangle size={10} className="text-yellow-500"/>
                                <span>Namensänderung max. 2x pro Monat möglich.</span>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input 
                                label="EA ID / Gamertag" 
                                value={formData.eaId} 
                                onChange={e => setFormData({...formData, eaId: e.target.value})}
                                placeholder="Dein In-Game Name"
                            />
                            <div>
                                <label className="block text-gray-400 text-sm font-bold mb-2 uppercase flex items-center gap-2">
                                    Discord ID 
                                    <div className="group relative">
                                        <HelpCircle size={14} className="text-gray-500 hover:text-white cursor-help"/>
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-48 bg-black border border-gray-700 p-2 rounded text-[10px] text-gray-300 hidden group-hover:block z-10 pointer-events-none">
                                            Benötigt für Bot-Pings.<br/>Discord: Einstellungen &gt; Erweitert &gt; Entwicklermodus AN. Dann Rechtsklick auf deinen Namen &gt; Nutzer-ID kopieren.
                                        </div>
                                    </div>
                                </label>
                                <input 
                                    className="w-full bg-gray-900 border border-gray-700 rounded py-2 px-3 text-white font-mono focus:border-f1-red outline-none transition-colors"
                                    placeholder="z.B. 2938475..."
                                    value={formData.discord_id}
                                    onChange={e => setFormData({...formData, discord_id: e.target.value})}
                                />
                            </div>
                        </div>
                    </div>

                    {/* NEW: THEME TOGGLE */}
                    <div className="pt-4 border-t border-gray-800">
                        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                            <Palette size={16} className="text-f1-red"/> Personalisierung
                        </h3>
                        <div className="flex items-center justify-between p-4 bg-gray-900 rounded border border-gray-800">
                            <div>
                                <div className="font-bold text-sm text-white">Team Design verwenden</div>
                                <div className="text-xs text-gray-500">
                                    Nutzt deine Team-Farbe als Akzentfarbe für die gesamte Webseite. Das Liga-Logo bleibt erhalten.
                                </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" className="sr-only peer" checked={teamThemeEnabled} onChange={toggleTeamTheme} />
                                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-f1-red rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-f1-red"></div>
                            </label>
                        </div>
                    </div>

                    <div className="pt-6 border-t border-gray-800">
                        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                            <Video size={16} className="text-f1-red"/> Streaming Channels
                        </h3>
                        <p className="text-xs text-gray-500 mb-4">Füge deine Kanäle hinzu, um auf der Stream-Wall gelistet zu werden.</p>
                        
                        <div className="space-y-4">
                            <div className="relative">
                                <Twitch className="absolute left-3 top-2.5 text-purple-500 w-5 h-5"/>
                                <input 
                                    className="w-full bg-gray-900 border border-gray-700 rounded py-2 pl-10 pr-3 text-white focus:border-purple-500 outline-none"
                                    placeholder="Twitch Username (z.B. LandoNorris)"
                                    value={formData.twitch_name}
                                    onChange={e => setFormData({...formData, twitch_name: e.target.value})}
                                />
                            </div>
                            <div className="relative">
                                <Youtube className="absolute left-3 top-2.5 text-red-500 w-5 h-5"/>
                                <input 
                                    className="w-full bg-gray-900 border border-gray-700 rounded py-2 pl-10 pr-3 text-white focus:border-red-500 outline-none"
                                    placeholder="YouTube Handle (z.B. @Formula1)"
                                    value={formData.youtube_handle}
                                    onChange={e => setFormData({...formData, youtube_handle: e.target.value})}
                                />
                            </div>
                            <div className="relative">
                                <div className="absolute left-3 top-2.5 bg-green-500 text-black font-black text-[10px] w-5 h-5 flex items-center justify-center rounded">K</div>
                                <input 
                                    className="w-full bg-gray-900 border border-gray-700 rounded py-2 pl-10 pr-3 text-white focus:border-green-500 outline-none"
                                    placeholder="Kick Username"
                                    value={formData.kick_name}
                                    onChange={e => setFormData({...formData, kick_name: e.target.value})}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="pt-6 border-t border-gray-800">
                        <h3 className="text-lg font-bold text-white mb-4 flex items-center justify-center gap-2">
                            <Lock size={16} className="text-f1-red"/> Sicherheit
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Input 
                                label="Neues Passwort" 
                                type="password" 
                                value={formData.password} 
                                onChange={e => setFormData({...formData, password: e.target.value})}
                                placeholder="Leer lassen zum Behalten"
                            />
                            <Input 
                                label="Passwort bestätigen" 
                                type="password" 
                                value={formData.confirmPassword} 
                                onChange={e => setFormData({...formData, confirmPassword: e.target.value})}
                                placeholder="Neues Passwort wiederholen"
                            />
                        </div>
                    </div>

                    <div className="pt-6 flex justify-end items-center">
                        <Button onClick={handleSave} disabled={loading} icon={Save}>
                            {loading ? 'Speichere...' : 'Profil aktualisieren'}
                        </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
};
